import React, { useState, useEffect } from 'react'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiEye, FiUsers, FiTrendingUp, FiDownload, FiShare2, FiCalendar } = FiIcons

const AnalyticsDashboard = ({ projectId }) => {
  const [analytics, setAnalytics] = useState(null)
  const [loading, setLoading] = useState(true)
  const [timeRange, setTimeRange] = useState('7d')

  useEffect(() => {
    // Simulate loading analytics data
    setTimeout(() => {
      setAnalytics({
        totalViews: 1247,
        uniqueViewers: 892,
        avgViewTime: '2m 34s',
        completionRate: 78,
        downloads: 23,
        shares: 45,
        viewsByDay: [
          { date: '2024-01-01', views: 120, uniqueViewers: 89 },
          { date: '2024-01-02', views: 145, uniqueViewers: 102 },
          { date: '2024-01-03', views: 189, uniqueViewers: 134 },
          { date: '2024-01-04', views: 167, uniqueViewers: 123 },
          { date: '2024-01-05', views: 201, uniqueViewers: 156 },
          { date: '2024-01-06', views: 178, uniqueViewers: 145 },
          { date: '2024-01-07', views: 247, uniqueViewers: 143 }
        ],
        topReferrers: [
          { source: 'Direct', visits: 456, percentage: 36.6 },
          { source: 'Twitter', visits: 234, percentage: 18.8 },
          { source: 'LinkedIn', visits: 189, percentage: 15.2 },
          { source: 'Email', visits: 167, percentage: 13.4 },
          { source: 'Other', visits: 201, percentage: 16.1 }
        ],
        deviceTypes: [
          { type: 'Desktop', count: 687, percentage: 55.1 },
          { type: 'Mobile', count: 398, percentage: 31.9 },
          { type: 'Tablet', count: 162, percentage: 13.0 }
        ]
      })
      setLoading(false)
    }, 1000)
  }, [projectId, timeRange])

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-gray-200 rounded w-1/3"></div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-24 bg-gray-200 rounded-lg"></div>
          ))}
        </div>
        <div className="h-64 bg-gray-200 rounded-lg"></div>
      </div>
    )
  }

  const StatCard = ({ icon, title, value, change, changeType }) => (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {change && (
            <div className={`flex items-center mt-2 text-sm ${
              changeType === 'positive' ? 'text-green-600' : 'text-red-600'
            }`}>
              <SafeIcon icon={FiTrendingUp} className="w-4 h-4 mr-1" />
              <span>{change}</span>
            </div>
          )}
        </div>
        <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
          <SafeIcon icon={icon} className="w-6 h-6 text-primary-600" />
        </div>
      </div>
    </div>
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h2>
        <div className="flex items-center space-x-4">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none bg-white"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          icon={FiEye}
          title="Total Views"
          value={analytics.totalViews.toLocaleString()}
          change="+12.5%"
          changeType="positive"
        />
        <StatCard
          icon={FiUsers}
          title="Unique Viewers"
          value={analytics.uniqueViewers.toLocaleString()}
          change="+8.2%"
          changeType="positive"
        />
        <StatCard
          icon={FiDownload}
          title="Downloads"
          value={analytics.downloads}
          change="+23.1%"
          changeType="positive"
        />
        <StatCard
          icon={FiShare2}
          title="Shares"
          value={analytics.shares}
          change="+15.7%"
          changeType="positive"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Views Chart */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Views Over Time</h3>
          <div className="h-64 flex items-end justify-between space-x-2">
            {analytics.viewsByDay.map((day, index) => (
              <div key={index} className="flex flex-col items-center flex-1">
                <div
                  className="w-full bg-primary-500 rounded-t"
                  style={{
                    height: `${(day.views / Math.max(...analytics.viewsByDay.map(d => d.views))) * 200}px`,
                    minHeight: '4px'
                  }}
                ></div>
                <span className="text-xs text-gray-500 mt-2">
                  {new Date(day.date).getDate()}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Device Types */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Device Types</h3>
          <div className="space-y-4">
            {analytics.deviceTypes.map((device, index) => (
              <div key={index}>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm font-medium text-gray-700">{device.type}</span>
                  <span className="text-sm text-gray-500">{device.percentage}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-primary-500 h-2 rounded-full"
                    style={{ width: `${device.percentage}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Detailed Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Referrers */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Referrers</h3>
          <div className="space-y-3">
            {analytics.topReferrers.map((referrer, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                  <span className="text-sm font-medium text-gray-700">{referrer.source}</span>
                </div>
                <div className="text-right">
                  <div className="text-sm font-semibold text-gray-900">{referrer.visits}</div>
                  <div className="text-xs text-gray-500">{referrer.percentage}%</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Performance Metrics */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-700">Average View Time</span>
              <span className="text-sm font-semibold text-gray-900">{analytics.avgViewTime}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-700">Completion Rate</span>
              <span className="text-sm font-semibold text-gray-900">{analytics.completionRate}%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-700">Bounce Rate</span>
              <span className="text-sm font-semibold text-gray-900">22%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-700">Return Visitors</span>
              <span className="text-sm font-semibold text-gray-900">34%</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AnalyticsDashboard